import { Layout } from "@/components/layout";
import { useToast } from "@/hooks/use-toast";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useBusinesses } from "@/hooks/use-businesses";
import { useLocation } from "wouter";
import { ArrowLeft, Building } from "lucide-react";

// Define form schema for business creation
const businessSchema = z.object({
  name: z.string().min(2, "Business name must be at least 2 characters"),
  description: z.string().optional(),
  industry: z.string().min(1, "Please select an industry"),
  logoUrl: z.string().optional(),
});

// Industry options
import { flattenIndustries } from "@shared/constants/industries";
const industries = flattenIndustries();

type BusinessFormValues = z.infer<typeof businessSchema>;

export default function CreateBusinessPageSimple() {
  const { createBusiness, isPendingCreate } = useBusinesses();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  // Initialize form with default values
  const form = useForm<BusinessFormValues>({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      name: "",
      description: "",
      industry: "",
      logoUrl: "",
    },
  });
  
  // Handle form submission
  const onSubmit = async (values: BusinessFormValues) => {
    console.log("[CreateBusiness] handler fired, payload:", values);
    try {
      await createBusiness(values);
      toast({ title: "Business created" });
      navigate("/dashboard");
    } catch (error: any) {
      console.error("[CreateBusiness] API error:", error);
      const details = error?.details;

      if (details?.code === "LICENSE_LIMIT") {
        toast({
          title: "Upgrade Required",
          description: "You've reached your business limit. Redirecting to billing...",
        });
        navigate(details.redirectTo || "/subscription");
        return;
      }

      toast({
        title: "Failed to create business",
        description: details?.message || error?.message || "Please try again",
        variant: "destructive",
      });
    }
  };

  const onInvalid = (errors: any) => {
    console.log("[CreateBusiness] validation failed:", errors);
    const first = Object.values(errors)[0] as any;
    toast({
      title: "Please fix form errors",
      description: first?.message || "Required fields are missing",
      variant: "destructive",
    });
  };
  
  return (
    <Layout>
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="icon" 
              className="mr-2" 
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-neutral-900">Create New Business</h1>
          </div>
          <p className="mt-1 text-sm text-neutral-500 ml-9">
            Set up your business information
          </p>
        </div>

        {/* Business Information Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="mr-2 h-5 w-5" />
              Business Information
            </CardTitle>
            <CardDescription>
              Enter your business details below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your business name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {industries.map((industry) => (
                            <SelectItem key={industry.value} value={industry.value}>
                              {industry.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Brief description of your business"
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="logoUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logo URL (Optional)</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="https://example.com/logo.png"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate("/dashboard")}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isPendingCreate}
                  >
                    {isPendingCreate ? "Creating..." : "Create Business"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}